import React, { FormEvent } from "react";
import { RoleHelper } from "src/helpers";
import { MAX_VENDORS_PER_ACCOUNT, MAX_USER_ACCOUNT_SUPERVISOR_EMAILS } from "src/constants";
import { ValidationErrors } from "src/errors";
import { CreateUserAccountFormData, CreateUserAccountFormDataKey, UserRole } from "src/types";
import { useScrollOnMessage } from "src/hooks";
import { UserSupervisorToolTip, SubmissionTooltip } from "src/components/tooltips";
import { DropdownSelector, TextInputBoxList, TextInputBox } from "src/components/ui/inputs";
import { FormLvlErrorMessage, ValidationErrorMessage } from "src/components/ui/feedback";
import { CollapsibleSection } from "src/components/ui/collapsible";

interface CreateUserAccountFormProps {
  formData: CreateUserAccountFormData;
  userRoleDisplayOptions: UserRole[];
  vendorsOptions: string[];
  validationErrors?: ValidationErrors<CreateUserAccountFormDataKey>;
  formLvlErrorMessage: string;
  isFormComplete: Boolean;
  handleInputChange: (
    field: CreateUserAccountFormDataKey,
    value: CreateUserAccountFormData[keyof CreateUserAccountFormData]
  ) => void;
  handleSubmit: (e: FormEvent) => void;
}

export const CreateUserAccountForm: React.FC<CreateUserAccountFormProps> = ({
  formData,
  userRoleDisplayOptions,
  vendorsOptions,
  validationErrors,
  formLvlErrorMessage,
  isFormComplete,
  handleInputChange,
  handleSubmit
}) => {
  const formLvlErrorMessageRef = useScrollOnMessage(formLvlErrorMessage);

  const handleSupervisorEmailsChange: React.Dispatch<React.SetStateAction<string[]>> = (emails) =>
    handleInputChange("supervisorEmails", emails as string[]);

  const handleVendorNamesChange: React.Dispatch<React.SetStateAction<string[]>> = (vendorNames) =>
    handleInputChange("vendorNames", vendorNames as string[]);

  const addEmptySupervisorEmail = () =>
    formData.supervisorEmails.length < MAX_USER_ACCOUNT_SUPERVISOR_EMAILS &&
    handleInputChange("supervisorEmails", [...formData.supervisorEmails, ""]);

  const addEmptyVendorName = () =>
    formData.vendorNames.length < MAX_VENDORS_PER_ACCOUNT &&
    handleInputChange("vendorNames", [...formData.vendorNames, ""]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto">
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
          {/* Header Section */}
          <div className="bg-black px-8 py-6">
            <h1 className="text-3xl font-bold text-white tracking-tight">Create User Account</h1>
            <p className="text-primary-100 mt-2 text-lg">Add a new user to the system</p>
          </div>

          <form onSubmit={handleSubmit} autoComplete="off" className="p-8">
            <div className="space-y-8">
              {/* Basic Information Card */}
              <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                <CollapsibleSection
                  initiallyOpen={true}
                  title={
                    <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-primary-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      Basic Information
                    </h3>
                  }
                >
                  <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Email</label>
                      <TextInputBox
                        id="user-eml"
                        onInputChange={(value: string) => handleInputChange("email", value)}
                        inputValue={formData.email}
                      />
                      <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("email")} />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Role</label>
                      <DropdownSelector
                        id="user-role"
                        options={userRoleDisplayOptions}
                        onSelect={(option: string) => handleInputChange("role", option as UserRole)}
                        value={formData.role}
                        placeholder="Select a role"
                      />
                      <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("role")} />
                    </div>

                    <div className="lg:col-span-2 space-y-2">
                      <label className="block text-sm font-medium text-secondary-700 mb-2">Name</label>
                      <TextInputBox
                        id="user-name"
                        onInputChange={(value: string) => handleInputChange("name", value)}
                        inputValue={formData.name}
                      />
                      <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("name")} />
                    </div>
                  </div>
                </CollapsibleSection>
              </div>

              {/* Healf Operations Team Section */}
              {RoleHelper.isHealfOperationsTeam(formData.role) && (
                <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                  <CollapsibleSection
                    initiallyOpen={true}
                    title={
                      <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
                        <svg
                          className="w-5 h-5 mr-2 text-primary-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h2M9 7h6m-6 4h6m-2 4h2"
                          />
                        </svg>
                        Healf Operations Team Details
                      </h3>
                    }
                  >
                    <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Job Title
                          <span className="text-secondary-500 text-xs ml-2">(optional)</span>
                        </label>
                        <TextInputBox
                          id="job-title"
                          onInputChange={(value: string) => handleInputChange("jobTitle", value)}
                          inputValue={formData.jobTitle}
                        />
                        <ValidationErrorMessage message={validationErrors?.getErrorMessageByFormDataKey("jobTitle")} />
                      </div>

                      <div className="lg:col-span-2 space-y-2">
                        <label className="flex items-center text-sm font-medium text-secondary-700 mb-2">
                          Supervisor Emails
                          <span className="text-secondary-500 text-xs ml-2">(optional)</span>
                          <div className="ml-2">
                            <UserSupervisorToolTip />
                          </div>
                        </label>
                        <TextInputBoxList
                          id="supervisor-email"
                          inputValueList={formData.supervisorEmails}
                          onInputChange={handleSupervisorEmailsChange}
                        />
                        <ValidationErrorMessage
                          message={validationErrors?.getErrorMessageByFormDataKey("supervisorEmails")}
                        />
                        <button
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg border border-primary-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={addEmptySupervisorEmail}
                          type="button"
                          disabled={formData.supervisorEmails.length >= MAX_USER_ACCOUNT_SUPERVISOR_EMAILS}
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          Add Supervisor Email
                        </button>
                      </div>
                    </div>
                  </CollapsibleSection>
                </div>
              )}

              {/* Vendor Assignment Section */}
              {formData.role === "VENDOR" && (
                <div className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden">
                  <CollapsibleSection
                    initiallyOpen={true}
                    title={
                      <h3 className="text-lg font-semibold text-secondary-800 flex items-center">
                        <svg
                          className="w-5 h-5 mr-2 text-primary-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h2M9 7h6m-6 4h6m-2 4h2"
                          />
                        </svg>
                        Vendor Assignment
                      </h3>
                    }
                  >
                    <div className="p-6 space-y-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-secondary-700 mb-2">Vendors</label>
                        <TextInputBoxList
                          id="vendor-name"
                          inputValueList={formData.vendorNames}
                          onInputChange={handleVendorNamesChange}
                          dropDownOptions={vendorsOptions?.filter((v) => !formData.vendorNames.includes(v))}
                        />
                        <ValidationErrorMessage
                          message={validationErrors?.getErrorMessageByFormDataKey("vendorNames")}
                        />
                        <button
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg border border-primary-200 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={addEmptyVendorName}
                          type="button"
                          disabled={formData.vendorNames.length >= MAX_VENDORS_PER_ACCOUNT}
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          Add Vendor
                        </button>
                      </div>
                    </div>
                  </CollapsibleSection>
                </div>
              )}
            </div>

            <div className="my-6">
              {validationErrors && validationErrors.length > 0 && (
                <div className="mb-6">
                  <ValidationErrorMessage message={validationErrors[0].message} />
                </div>
              )}
            </div>

            {/* Form Level Error Message */}
            <div className="mt-6">
              <FormLvlErrorMessage message={formLvlErrorMessage} centred={false} ref={formLvlErrorMessageRef} />
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-start items-center pt-6 border-t border-secondary-200">
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <button
                  className={`px-8 py-3 rounded-lg font-semibold text-white transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    !isFormComplete
                      ? "bg-secondary-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-success-600 to-success-700 hover:from-success-700 hover:to-success-800 focus:ring-success-500 shadow-lg hover:shadow-xl"
                  }`}
                  disabled={!isFormComplete}
                  type="submit"
                >
                  <span className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Create User Account
                  </span>
                </button>
              </div>

              <div className="flex-shrink-0">
                <SubmissionTooltip formState={!isFormComplete ? "INCOMPLETE" : "NO_CHANGE"} />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
